import type { ComponentType } from 'react';
import {
    useController,
    type ControllerRenderProps,
    type FieldValues,
    type Control,
    type Path,
} from 'react-hook-form';

export interface ControlledInputProps<TFieldValues extends FieldValues> {
    /**
     * Modifies value provided by form to input compatible format (form → input)
     * */
    format?: (value: TFieldValues[Path<TFieldValues>]) => any;
    /**
     * Modifies value provided by input to form compatible format (form ← input)
     * */
    parse?: (value: any) => unknown;
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
}

export function useEnhancedController<TFieldValues extends FieldValues>({
    format,
    parse,
    ...props
}: ControlledInputProps<TFieldValues>): Omit<ControllerRenderProps<TFieldValues>, 'disabled'> & {
    description?: string;
    invalid?: boolean;
} {
    const {
        field: { disabled, ...field },
        fieldState: { invalid, isTouched, error },
    } = useController<TFieldValues>(props);

    const displayError = invalid && (isTouched || (error as any)?.includes('too long'));

    return {
        ...field,
        ...(displayError && { invalid: true, description: error }),
        ...(parse && { onChange: (value: any) => field.onChange(parse(value)) }),
        ...(format && { value: format(field.value) }),
    };
}

function controlled<InputProps>(Input: ComponentType<InputProps>, type = 'input') {
    function ControlledInput<TFieldValues extends FieldValues>({
        control,
        format,
        parse,
        name,
        ...props
    }: ControlledInputProps<TFieldValues> & Omit<InputProps, 'value' | 'onChange'>) {
        const field = useEnhancedController<TFieldValues>({ control, name, format, parse });
        const id = `${field.name}-${type}`;

        return <Input {...(props as InputProps)} {...field} id={id} />;
    }

    const displayName = Input.displayName || Input.name || 'Input';
    ControlledInput.displayName = `controlled(${displayName})`;

    return ControlledInput;
}

export default controlled;
